import { Trans, useLingui } from '@lingui/react/macro';

import { getExceptionalCoverageRequestLabelType } from '@/components/DefectsList/DefectCard/ExceptionalCoverageToggle';
import { Link } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import IconValidation from '@/icons/Validation.svg';
import IconValidationWaiting from '@/icons/ValidationWaiting.svg';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { EXCEPTIONAL_BRAND_COVERAGE_REASONS } from '@/models/article';
import { ArticleDefectWithRelations, ArticleWithRelations } from '@/models/request';
import useViewPort from '@/utils/useViewport';

export const ExceptionalBrandCoverage = ({
  article,
  defect,
}: {
  article: ArticleWithRelations;
  defect: ArticleDefectWithRelations;
}) => {
  const { isMobile } = useViewPort();
  const { i18n } = useLingui();
  const { request } = useBrandRequestContext();

  const isGranted = getExceptionalCoverageRequestLabelType(article) === 'granted';
  const Icon = isGranted ? IconValidation : IconValidationWaiting;

  const reason = EXCEPTIONAL_BRAND_COVERAGE_REASONS.find(
    ({ id }) => id === defect.exceptionalBrandResponsibilityCoverageReason
  );
  const originalRequest = defect.exceptionalBrandResponsibilityCoverageOriginalRequest;
  const originalRequestHref =
    originalRequest?.clientToken &&
    `/brand/${request.organization.slug}/requests/${originalRequest.id}?clientToken=${originalRequest.clientToken}`;

  const details = [reason ? i18n._(reason.text) : null, originalRequest?.reference]
    .filter(Boolean)
    .join(' • ');

  const label = isGranted ? (
    <Trans id="client.article.services.column.defect.exceptional-brand-coverage-granted">
      Covered by brand
    </Trans>
  ) : (
    <Trans id="client.article.services.column.defect.exceptional-brand-coverage-requested">
      Exceptional brand coverage requested
    </Trans>
  );

  const content = (
    <>
      {label}
      {!!details && (
        <>
          {' ('}
          {!!reason && i18n._(reason.text)}
          {!!reason && !!originalRequest && ' • '}
          {!!originalRequest &&
            (originalRequestHref ? (
              <Link
                to={originalRequestHref}
                style={{ textDecoration: 'underline', font: 'inherit', color: 'inherit' }}
              >
                {originalRequest.reference}
              </Link>
            ) : (
              originalRequest.reference
            ))}
          {')'}
        </>
      )}
    </>
  );

  if (isMobile) {
    const tooltipContent = details ? (
      <>
        {label} ({details})
      </>
    ) : (
      label
    );

    return (
      <Tooltip content={tooltipContent}>
        {originalRequestHref ? (
          <Link to={originalRequestHref}>
            <Icon className="text-secondary" style={{ fontSize: '1rem' }} />
          </Link>
        ) : (
          <Button variant="style-less">
            <Icon className="text-secondary" style={{ fontSize: '1rem' }} />
          </Button>
        )}
      </Tooltip>
    );
  }

  return (
    <Stack row alignItems="flex-start" gap="0.25rem" flexWrap="nowrap">
      <Icon className="text-secondary" />
      <InlineText color="secondary" size="200">
        {content}
      </InlineText>
    </Stack>
  );
};
